/**
 * CapabilityWorkbench.tsx — the generic action control loop, end to end.
 *
 * One component, seven stages, one descriptor:
 *
 *   INSPECT → CONFIGURE → PREVIEW → REVIEW → APPLY → READBACK → DIAGNOSTICS
 *
 * The reason there is ONE of these rather than one per discipline is the
 * architectural rule the Engine owns: P&ID editing, electrical editing, panel
 * editing, ATEX editing and safety editing differ in their DESCRIPTORS, not in
 * their interaction. The moment the Engine publishes an ATEX capability, this
 * workbench runs the same loop for it with no source edit here — which is the
 * property WP19's P57 acceptance asks for and the reason a discipline-specific
 * screen would be a defect rather than a feature.
 *
 * WHAT THE LOOP REFUSES TO DO
 * --------------------------
 *   • Recalculate anything. The preview is the Engine's answer; this component
 *     renders it and has no arithmetic anywhere.
 *   • Apply without a preview the human has seen. Apply is only offered after a
 *     PREVIEW THAT SUCCEEDED, because applying a change nobody previewed is the
 *     one thing a reviewed workflow exists to prevent.
 *   • Patch its own picture. After apply, the loop re-reads the project and
 *     renders what the Engine now says. The state on screen is the canonical
 *     state or it is not on screen.
 *   • Self-approve. When the Engine's own review policy names an independent
 *     human, this screen says so and routes the user to the change set rather
 *     than offering an approval it has no authority to grant.
 *   • Hide a refusal. A failed stage is displayed with the Engine's own
 *     diagnostics, verbatim, and the loop stops there.
 */

import { useCallback, useMemo, useState } from "react";
import { useClient } from "../../api/ClientContext";
import {
  buildReadRequest,
  buildRequest,
  inspect,
  readOutcome,
  validateValues,
  type ActionPlan,
  type FieldValues,
  type Outcome,
} from "../../api/capabilityAction";
import type { CapabilityManifest } from "../../api/capabilityManifest";
import { ErrorBanner } from "../../components/ErrorBanner";
import { Empty, Panel } from "../../components/Panel";
import { useAsync } from "../../stores/useAsync";
import { DeterminationStates } from "../assessments/DeterminationStates";
import { ChangeSetPanel } from "../changeSets/ChangeSetPanel";
import { useChangeSet } from "../changeSets/useChangeSet";
import { LockReconciliation, lockVerdict } from "../locks/LockReconciliation";
import { GenericForm } from "./GenericForm";
import { ValueTree } from "./ValueTree";

/** The stages a run has passed, so the header can show where it is. */
type Stage = "inspect" | "configure" | "preview" | "review" | "apply" | "readback" | "diagnostics";

const STAGES: readonly Stage[] = ["inspect", "configure", "preview", "review", "apply", "readback", "diagnostics"];

/**
 * The dispatch, and nothing else.
 *
 * Split from the loop so that the early returns below happen BEFORE any hook
 * runs in the child. React requires hooks to run unconditionally, and a
 * capability that is addressable on one render and not the next must not change
 * the hook order of the screen that drives it.
 */
export function CapabilityWorkbench({ projectId, manifest, capabilityId }: {
  projectId: string;
  manifest: CapabilityManifest;
  capabilityId: string;
}): JSX.Element {
  const resolution = useMemo(() => {
    const descriptor = [...manifest.capabilities, ...manifest.projections].find(d => d.id === capabilityId);
    return descriptor ? inspect(manifest, descriptor) : undefined;
  }, [capabilityId, manifest]);

  if (!resolution) {
    return (
      <Panel title="Capability">
        <Empty>
          The Engine&apos;s manifest does not publish <code>{capabilityId}</code>. It may have been withdrawn, or this
          workbench may be pointed at a different Engine.
        </Empty>
      </Panel>
    );
  }

  if (!resolution.addressable) {
    const { descriptor, unavailable } = resolution.unaddressable;
    return (
      <Panel title={descriptor.id} tone="warn">
        <p><strong>This capability is not usable from this browser.</strong></p>
        <dl className="facts">
          <dt>Capability</dt><dd><code>{descriptor.id}</code> v{descriptor.idVersion}</dd>
          <dt>Transport</dt><dd><code>{unavailable.transport}</code></dd>
          <dt>Missing</dt><dd>{unavailable.missing}</dd>
        </dl>
        <p className="muted">{unavailable.detail}</p>
        <p className="muted">{descriptor.presentation.summary}</p>
      </Panel>
    );
  }

  return <ActionLoop projectId={projectId} plan={resolution.plan} />;
}

/** The loop itself. Every hook below runs unconditionally for an addressable plan. */
function ActionLoop({ projectId, plan }: { projectId: string; plan: ActionPlan }): JSX.Element {
  const client = useClient();
  const changeSets = useChangeSet(projectId);

  const [values, setValues] = useState<FieldValues>({});
  const [stage, setStage] = useState<Stage>("configure");
  const [preview, setPreview] = useState<{ plan: ActionPlan; outcome: Outcome } | undefined>(undefined);
  /** What the Engine answered to the APPLY, kept so the readback is reviewable. */
  const [appliedOutcome, setAppliedOutcome] = useState<Outcome | undefined>(undefined);
  const [applied, setApplied] = useState(false);
  const [readback, setReadback] = useState<unknown>(undefined);
  const [diagnostics, setDiagnostics] = useState<Outcome | undefined>(undefined);
  const [error, setError] = useState<unknown>(undefined);

  const project = useAsync(() => client.projectStatus(projectId), [client, projectId], {
    // Readback must actually re-read, so the reload after apply is the point.
    enabled: applied,
  });
  // The change set, re-read after an apply. This is the only thing that can say
  // the operation was RECORDED, because `applied` is documented as always false.
  const recorded = useAsync(
    async () => (changeSets.changeSet ? client.getChangeSet(projectId, changeSets.changeSet.changeSetId) : undefined),
    [changeSets.changeSet, client, projectId],
    { enabled: applied },
  );

  const reset = useCallback(() => {
    setPreview(undefined);
    setAppliedOutcome(undefined);
    setReadback(undefined);
    setDiagnostics(undefined);
    setApplied(false);
    setError(undefined);
  }, []);

  const problems = validateValues(plan, values);
  const canSubmit = problems.length === 0 && !changeSets.busy;

  const onPreview = useCallback(async () => {
    if (!canSubmit) return;
    setError(undefined);
    setApplied(false);
    setReadback(undefined);
    try {
      const target = await changeSets.ensure(`${plan.descriptor.id} via the capability workbench`);
      const request = buildRequest(plan, values, { phase: "preview", projectId, changeSetId: target.changeSetId });
      const raw = await client.capabilityPreview(projectId, target.changeSetId, request.body as never);
      const outcome = readOutcome(plan.descriptor, raw);
      setPreview({ plan, outcome });
      setStage("review");
    } catch (cause) {
      setError(cause);
      setStage("diagnostics");
    }
  }, [canSubmit, changeSets, client, plan, projectId, values]);

  const onApply = useCallback(async () => {
    // Three independent gates, and all three must open. The third is WP30: a
    // regeneration that meets a lock stops here, visibly, rather than skipping
    // the locked subject or overwriting it.
    if (!preview || !preview.outcome.accepted) return;
    if (lockVerdict(preview.outcome.raw).blocksApply) return;
    setError(undefined);
    try {
      const target = await changeSets.ensure(`${plan.descriptor.id} via the capability workbench`);
      const request = buildRequest(plan, values, { phase: "apply", projectId, changeSetId: target.changeSetId });
      const raw = await client.capabilityApply(projectId, target.changeSetId, request.body as never);
      // THE ACCEPTANCE FLAG, NOT `applied`.
      //
      // The first version of this workbench read `applied !== true` as failure,
      // and against a real Engine that reported EVERY successful apply as "The
      // Engine did not record this change. Nothing was applied." — because
      // `applied` is documented as ALWAYS false on this route: recording is
      // working state inside the change set, and only publication writes
      // canonical state. A unit test built on a fixture that returned
      // `applied: true` could never have found it, which is the whole argument
      // for running the real thing.
      //
      // So acceptance is the same single required boolean the result schema
      // declares — read by `readOutcome`, the same way the preview reads it — and
      // a change that did not record shows the Engine's own diagnostics rather
      // than a browser's guess about a field whose contract is "always false".
      const outcome = readOutcome(plan.descriptor, raw);
      if (!outcome.accepted) {
        setError(new Error(
          "The Engine did not accept this change, so nothing was recorded in the change set. "
          + "Its own diagnostics are shown below, verbatim.",
        ));
        setDiagnostics(outcome);
        setStage("diagnostics");
        return;
      }
      setApplied(true);
      setAppliedOutcome(outcome);
      setStage("readback");
      project.reload();
      recorded.reload();
    } catch (cause) {
      setError(cause);
      setStage("diagnostics");
    }
  }, [client, plan, preview, project, projectId, values, changeSets, recorded]);

  const onRead = useCallback(async () => {
    if (!canSubmit) return;
    setError(undefined);
    try {
      const request = buildReadRequest(plan, values);
      const raw = await client.capabilityRead(projectId, request.body);
      setReadback(raw);
      setStage("diagnostics");
    } catch (cause) {
      setError(cause);
      setStage("diagnostics");
    }
  }, [canSubmit, client, plan, projectId, values]);

  const isReadOnly = plan.phases.length === 1 && plan.phases[0] === "read";
  // Read the Engine's own lock report off the PREVIEW, before anything is
  // staged. It is computed from the same bytes the reviewer is looking at, so
  // the block cannot lag behind what is displayed.
  const locks = useMemo(
    () => lockVerdict(preview?.outcome.raw),
    [preview],
  );

  return (
    <div className="capability-workbench">
      <StageBar stage={stage} />

      <Panel
        title="Inspect"
        tone={plan.humanReview ? "warn" : "default"}
        // Stable hooks so a real browser test can address one stage of the loop
        // without matching on heading text, which CSS transforms can rewrite.
        testId="stage-inspect"
        actions={<button type="button" className="ghost" onClick={reset}>Reset</button>}
      >
        <dl className="facts">
          <dt>Capability</dt><dd><code>{plan.descriptor.id}</code> v{plan.descriptor.idVersion}</dd>
          <dt>Kind / mode</dt><dd>{plan.descriptor.kind} · {plan.descriptor.mode}</dd>
          <dt>Addressed as</dt><dd><code>{plan.address}</code></dd>
          <dt>Transport</dt><dd><code>{plan.transport.transport}</code> — {plan.transport.phases.join(", ")}</dd>
          <dt>Permission</dt><dd><code>{plan.descriptor.permission}</code></dd>
          <dt>Risk</dt><dd>{plan.descriptor.risk}</dd>
          <dt>Review policy</dt><dd>{plan.descriptor.review}</dd>
          <dt>Generated from</dt><dd><code>{plan.descriptor.generatedFrom || "—"}</code></dd>
          <dt>Descriptor digest</dt><dd className="request-id">{plan.descriptor.digest}</dd>
        </dl>
        <p>{plan.descriptor.presentation.summary}</p>
        {plan.humanReview && (
          <p className="bad">
            This capability carries the Engine&apos;s own <code>{plan.descriptor.review}</code> policy. It can be
            previewed and staged here; it cannot be self-approved on this screen, because the Engine owns that
            boundary and this browser does not.
          </p>
        )}
        {plan.descriptor.applicability.requires.length > 0 && (
          <>
            <h3>The Engine says it requires</h3>
            <ul>{plan.descriptor.applicability.requires.map(item => <li key={item}>{item}</li>)}</ul>
          </>
        )}
        {plan.descriptor.applicability.canonicalInputs.length > 0 && (
          <>
            <h3>Canonical inputs it reads</h3>
            <ul>{plan.descriptor.applicability.canonicalInputs.map(item => <li key={item}><code>{item}</code></li>)}</ul>
          </>
        )}
      </Panel>

      <Panel title="Configure" testId="stage-configure">
        <GenericForm
          plan={plan}
          values={values}
          problems={problems}
          onChange={setValues}
          disabled={changeSets.busy}
        />
        {!plan.schemaComplete && (
          <p className="muted">
            The Engine&apos;s schema for this capability allows properties it does not describe. This form offers
            what was published, and the Engine validates the rest.
          </p>
        )}
        <div className="row">
          {isReadOnly ? (
            <button type="button" onClick={() => void onRead()} disabled={!canSubmit}>Run</button>
          ) : (
            <>
              <button type="button" onClick={() => void onPreview()} disabled={!canSubmit}>Preview</button>
              <button
                type="button"
                className="ghost"
                onClick={() => void onRead()}
                disabled={!canSubmit}
              >
                Run without the change set
              </button>
            </>
          )}
        </div>
      </Panel>

      {preview && (
        <Panel
          title="Review"
          testId="stage-review"
          tone={preview.outcome.accepted ? "default" : "error"}
          actions={
            <>
              <button
                type="button"
                onClick={() => void onApply()}
                // Apply is offered ONLY on an accepted preview with no unresolved
                // lock. A refused or unread outcome is not a smaller yes; it is
                // not a yes.
                disabled={!preview.outcome.accepted || locks.blocksApply || changeSets.busy}
              >
                Apply to the change set
              </button>
              <button type="button" className="ghost" onClick={reset}>Discard</button>
            </>
          }
        >
          <p className="muted">
            What the Engine resolved. Nothing here was calculated in this browser.
          </p>
          <OutcomeView outcome={preview.outcome} />
          <LockReconciliation verdict={locks} disabled={changeSets.busy} />
          <DeterminationStates
            resultSchema={plan.descriptor.resultSchema}
            payload={preview.outcome.raw as Record<string, unknown>}
            title="Determination in the Engine's own result"
          />
        </Panel>
      )}

      {applied && (
        <Panel title="Readback" testId="stage-readback" tone="default">
          <p className="muted">
            Re-read from the Engine. Nothing below is what this browser predicted: it is what the Engine now says,
            asked again over the wire.
          </p>
          {appliedOutcome && (
            <>
              <p>
                {appliedOutcome.acceptanceField === undefined ? (
                  <span className="bad">
                    The Engine published no acceptance flag on this result, so this browser cannot tell whether the
                    change was recorded. The raw result is shown and nothing is claimed.
                  </span>
                ) : (
                  <span className="good">
                    The Engine accepted the change: <code>{appliedOutcome.acceptanceField}</code> is true. The
                    change is <strong>recorded in the change set</strong>, not published — publication is a separate
                    reviewed step, and this browser does not perform it.
                  </span>
                )}
              </p>
              <OutcomeView outcome={appliedOutcome} />
            </>
          )}
          <h3>What the Engine says the change set now holds</h3>
          {recorded.loading ? <Empty>Re-reading the change set…</Empty> : (
            <ValueTree value={recorded.data ?? recorded.error} schema={undefined} highlight={[]} />
          )}
          <h3>What the Engine now says the project is</h3>
          {project.loading ? <Empty>Re-reading the project…</Empty> : (
            <ValueTree value={project.data ?? project.error} schema={undefined} highlight={[]} />
          )}
        </Panel>
      )}

      {(readback !== undefined || diagnostics !== undefined || error !== undefined) && (
        <Panel title="Diagnostics" testId="stage-diagnostics" tone={error !== undefined ? "error" : "default"}>
          <ErrorBanner error={error} onDismiss={() => setError(undefined)} />
          {diagnostics !== undefined && <OutcomeView outcome={diagnostics} />}
          {readback !== undefined && (
            <>
              <p className="muted">Returned by the Engine, verbatim.</p>
              <ValueTree value={readback} schema={undefined} highlight={[]} />
            </>
          )}
        </Panel>
      )}

      {!isReadOnly && <ChangeSetPanel controller={changeSets} />}
    </div>
  );
}

/**
 * Where the run is.
 *
 * Present because a reviewed workflow that gives no sense of position is one
 * people learn to click through. Each stage is a fact about what has happened,
 * not a promise about what will.
 */
function StageBar({ stage }: { stage: Stage }): JSX.Element {
  const index = STAGES.indexOf(stage);
  return (
    <ol className="stage-bar" aria-label="Action progress">
      {STAGES.map((name, position) => (
        <li key={name} className={position <= index ? "is-reached" : undefined} aria-current={name === stage ? "step" : undefined}>
          {name}
        </li>
      ))}
    </ol>
  );
}

/**
 * The Engine's answer, read against the descriptor that asked for it.
 *
 * The acceptance field is named, not assumed: it is whatever the Engine's own
 * `resultSchema` required. When the Engine published no acceptance flag this
 * says so, because "the browser cannot tell whether that succeeded" is a
 * materially different thing from "it did not".
 */
function OutcomeView({ outcome }: { outcome: Outcome }): JSX.Element {
  return (
    <>
      <p>
        {outcome.acceptanceField === undefined ? (
          <span className="bad">
            The Engine published no acceptance flag for this result, so this browser cannot tell whether it
            succeeded. The raw result is shown below and nothing may be staged from it.
          </span>
        ) : outcome.accepted ? (
          <span className="good">The Engine accepted this: <code>{outcome.acceptanceField}</code> is true.</span>
        ) : (
          <span className="bad">
            The Engine did not accept this: <code>{outcome.acceptanceField}</code> is not true. Its own
            diagnostics are below, verbatim.
          </span>
        )}
      </p>
      {outcome.diagnostics.map(entry => (
        <div key={entry.field}>
          <h3><code className="verbatim">{entry.field}</code></h3>
          <ul className="findings">
            {entry.entries.map((message, index) => <li key={index}><span>{message}</span></li>)}
          </ul>
        </div>
      ))}
      {outcome.diff.map(entry => (
        <div key={entry.field}>
          <h3><code className="verbatim">{entry.field}</code></h3>
          <ol className="normalized">
            {entry.entries.map((item, index) => (
              <li key={index}><ValueTree value={item} schema={undefined} highlight={[]} /></li>
            ))}
          </ol>
        </div>
      ))}
    </>
  );
}
